import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { formatCurrencyTotals, newFinanceService } from '../../services/newFinance.service';
import type { FinanceRecord } from '../../types/newFinance';
import { EXPENSE_CATEGORY_LABELS, OTHER_INCOME_CATEGORY_LABELS } from '../../types/newFinance';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  record: FinanceRecord;
}

export const FinanceDetailModal: React.FC<Props> = ({ isOpen, onClose, record }) => {
  const [originalRecord, setOriginalRecord] = useState<FinanceRecord | null>(null);
  const [isLoadingOriginal, setIsLoadingOriginal] = useState(false);

  // 수정된 기록인 경우 원본 데이터 로드
  useEffect(() => {
    const loadOriginalRecord = async () => {
      if (record.status === 'reapproval' && record.originalId) {
        setIsLoadingOriginal(true);
        try {
          const original = await newFinanceService.getFinanceRecordById(record.originalId);
          setOriginalRecord(original);
        } catch (error) {
          console.error('원본 기록 로드 실패:', error);
        } finally {
          setIsLoadingOriginal(false);
        }
      } else {
        setOriginalRecord(null);
      }
    };

    if (isOpen) {
      loadOriginalRecord();
    }
  }, [record, isOpen]);

  // 변경 상태 확인 함수
  const getChangeStatus = (originalItems: any[], currentItems: any[], index: number): string => {
    if (!originalRecord) return '';
    
    const originalItem = originalItems[index];
    const currentItem = currentItems[index];
    
    // 새로 추가된 항목
    if (!originalItem && currentItem) {
      return '추가된 내역';
    }
    
    // 삭제된 항목 (원본에는 있지만 현재에는 없음)
    if (originalItem && !currentItem) {
      return '삭제된 내역';
    }
    
    // 수정된 항목
    if (originalItem && currentItem && JSON.stringify(originalItem) !== JSON.stringify(currentItem)) {
      return '수정된 내역';
    }
    
    return '';
  };

  if (!isOpen) return null;

  const getStatusInfo = (status: string) => {
    const styles = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
      approved: { bg: 'bg-green-100', text: 'text-green-800' },
      reapproval: { bg: 'bg-orange-100', text: 'text-orange-800' }
    };

    const labels = {
      pending: '승인 대기',
      approved: '승인 완료',
      reapproval: '재승인 요청'
    };

    const style = styles[status as keyof typeof styles];
    
    return {
      ...style,
      label: labels[status as keyof typeof labels] || status
    };
  };

  const statusInfo = getStatusInfo(record.status);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[85vh] overflow-hidden shadow-lg">
        {/* 간단한 헤더 */}
        <div className="bg-blue-500 px-6 py-4 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div>
                <h2 className="text-lg font-semibold text-white">재정 상세보기</h2>
                <p className="text-blue-100 text-sm">{record.date}</p>
              </div>
              <div className={`px-2 py-1 rounded text-xs font-medium ${statusInfo.bg} ${statusInfo.text}`}>
                {statusInfo.label}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-200"
            >
              <FaTimes className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* 본문 - 3섹션 레이아웃 */}
        <div className="p-6 overflow-y-auto max-h-[calc(85vh-80px)]">
          <div className="space-y-6">
            
            {/* 섹션 1: 요약 정보 */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">금액 요약</h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-green-100 dark:bg-green-900/30 rounded p-3">
                  <div className="text-xs text-green-600 dark:text-green-400 mb-1">총 수입</div>
                  <div className="text-sm font-semibold text-green-800 dark:text-green-200">
                    {formatCurrencyTotals(record.incomeTotals)}
                  </div>
                </div>
                <div className="bg-red-100 dark:bg-red-900/30 rounded p-3">
                  <div className="text-xs text-red-600 dark:text-red-400 mb-1">총 지출</div>
                  <div className="text-sm font-semibold text-red-800 dark:text-red-200">
                    {formatCurrencyTotals(record.expenseTotals)}
                  </div>
                </div>
                <div className="bg-blue-100 dark:bg-blue-900/30 rounded p-3">
                  <div className="text-xs text-blue-600 dark:text-blue-400 mb-1">순 손익</div>
                  <div className="text-sm font-semibold text-blue-800 dark:text-blue-200">
                    {formatCurrencyTotals(record.netTotals)}
                  </div>
                </div>
              </div>
            </div>

            {/* 섹션 2: 수입 내역 */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">수입 내역</h3>
              <div className="space-y-4">
                
                {/* 십일조 */}
                {record.titheItems.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">십일조</h4>
                    <div className="space-y-2">
                      {record.titheItems.map((item, index) => {
                        const changeStatus = getChangeStatus(originalRecord?.titheItems || [], record.titheItems, index);
                        
                        return (
                          <div key={index} className="flex items-center bg-white dark:bg-gray-600 p-2 rounded text-sm space-x-3">
                            <div className="flex-1">
                              <span className="text-gray-800 dark:text-gray-200">{item.name}</span>
                            </div>
                            <div className="flex-1 text-center">
                              {changeStatus && (
                                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                                  changeStatus === '추가된 내역' ? 'bg-green-100 text-green-800' :
                                  changeStatus === '삭제된 내역' ? 'bg-red-100 text-red-800' :
                                  changeStatus === '수정된 내역' ? 'bg-orange-100 text-orange-800' : ''
                                }`}>
                                  {changeStatus}
                                </span>
                              )}
                            </div>
                            <div className="flex-1 text-right">
                              <span className="font-medium text-gray-900 dark:text-white">
                                {item.amount.toLocaleString()} {item.currency}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* 감사헌금 */}
                {record.thanksgivingItems.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">감사헌금</h4>
                    <div className="space-y-2">
                      {record.thanksgivingItems.map((item, index) => {
                        const changeStatus = getChangeStatus(originalRecord?.thanksgivingItems || [], record.thanksgivingItems, index);
                        
                        return (
                          <div key={index} className="flex items-center bg-white dark:bg-gray-600 p-2 rounded text-sm space-x-3">
                            <div className="flex-1">
                              <span className="text-gray-800 dark:text-gray-200">{item.name}</span>
                            </div>
                            <div className="flex-1 text-center">
                              {changeStatus && (
                                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                                  changeStatus === '추가된 내역' ? 'bg-green-100 text-green-800' :
                                  changeStatus === '삭제된 내역' ? 'bg-red-100 text-red-800' :
                                  changeStatus === '수정된 내역' ? 'bg-orange-100 text-orange-800' : ''
                                }`}>
                                  {changeStatus}
                                </span>
                              )}
                            </div>
                            <div className="flex-1 text-right">
                              <span className="font-medium text-gray-900 dark:text-white">
                                {item.amount.toLocaleString()} {item.currency}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* 기타헌금 */}
                {record.otherIncomeItems.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">기타헌금</h4>
                    <div className="space-y-2">
                      {record.otherIncomeItems.map((item, index) => {
                        const changeStatus = getChangeStatus(originalRecord?.otherIncomeItems || [], record.otherIncomeItems, index);
                        
                        return (
                          <div key={index} className="flex items-center bg-white dark:bg-gray-600 p-2 rounded text-sm space-x-3">
                            <div className="flex-1">
                              <div className="text-gray-800 dark:text-gray-200">
                                {item.name || item.description || '-'}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {OTHER_INCOME_CATEGORY_LABELS[item.category]}
                              </div>
                            </div>
                            <div className="flex-1 text-center">
                              {changeStatus && (
                                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                                  changeStatus === '추가된 내역' ? 'bg-green-100 text-green-800' :
                                  changeStatus === '삭제된 내역' ? 'bg-red-100 text-red-800' :
                                  changeStatus === '수정된 내역' ? 'bg-orange-100 text-orange-800' : ''
                                }`}>
                                  {changeStatus}
                                </span>
                              )}
                            </div>
                            <div className="flex-1 text-right">
                              <span className="font-medium text-gray-900 dark:text-white">
                                {item.amount.toLocaleString()} {item.currency}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 섹션 3: 지출 내역 및 작성자 정보 */}
            <div className="space-y-4">
              {/* 지출 내역 */}
              {record.expenseItems.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">지출 내역</h3>
                  <div className="space-y-2">
                    {record.expenseItems.map((item, index) => {
                      const changeStatus = getChangeStatus(originalRecord?.expenseItems || [], record.expenseItems, index);
                      
                      return (
                        <div key={index} className="flex items-center bg-white dark:bg-gray-600 p-2 rounded text-sm space-x-3">
                          <div className="flex-1">
                            <div className="text-gray-800 dark:text-gray-200">{EXPENSE_CATEGORY_LABELS[item.category]}</div>
                            {item.description && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">{item.description}</div>
                            )}
                          </div>
                          <div className="flex-1 text-center">
                            {changeStatus && (
                              <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                                changeStatus === '추가된 내역' ? 'bg-green-100 text-green-800' :
                                changeStatus === '삭제된 내역' ? 'bg-red-100 text-red-800' :
                                changeStatus === '수정된 내역' ? 'bg-orange-100 text-orange-800' : ''
                              }`}>
                                {changeStatus}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 text-right">
                            <span className="font-medium text-gray-900 dark:text-white">
                              {item.amount.toLocaleString()} {item.currency}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 작성자 정보 */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">작성 정보</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">작성자</span>
                    <span className="text-gray-900 dark:text-white">{record.createdBy}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">작성일시</span>
                    <span className="text-gray-900 dark:text-white">{record.createdAt.toLocaleString()}</span>
                  </div>
                  {record.modifiedBy && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">수정자</span>
                        <span className="text-gray-900 dark:text-white">{record.modifiedBy}</span>
                      </div>
                      {record.modifiedAt && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">수정일시</span>
                          <span className="text-gray-900 dark:text-white">{record.modifiedAt.toLocaleString()}</span>
                        </div>
                      )}
                      {record.modificationReason && (
                        <div className="mt-2 p-2 bg-orange-100 dark:bg-orange-900/30 rounded text-xs">
                          <span className="text-orange-700 dark:text-orange-300">수정 사유: {record.modificationReason}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 간단한 푸터 */}
        <div className="bg-gray-100 dark:bg-gray-700 px-6 py-3 rounded-b-lg border-t border-gray-200 dark:border-gray-600">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};